import React from "react";
import { Download, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { resources, faqs } from "../mock";

const Resources = () => {
  const { toast } = useToast();
  const grab = (title) => toast({ title: "Resource ready 📄", description: `“${title}” would download here. (Demo)` });

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Resources</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">Guides & tools for every family</h1>
          <p className="mt-5 text-lg text-muted-foreground">Free, expert-reviewed resources to support you through the early years.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.title} delay={(i % 3) * 80}>
                <div className="card-hover bg-white rounded-2xl p-7 shadow-soft h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                    <Badge variant="outline" className="rounded-full font-semibold">{r.category}</Badge>
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground">{r.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{r.text}</p>
                  <Button onClick={() => grab(r.title)} variant="outline" className="btn-cta mt-5 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full font-bold self-start">
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-secondary/40">
        <div className="container-prc max-w-3xl">
          <Reveal className="text-center mb-12">
            <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-4">FAQ</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Frequently asked questions</h2>
          </Reveal>
          <Reveal delay={100}>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((f) => (
                <AccordionItem key={f.q} value={f.q} className="bg-white rounded-2xl px-6 shadow-soft border-none">
                  <AccordionTrigger className="font-display font-bold text-left text-foreground hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl text-foreground">Still have questions?</h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Our friendly team is here to help you find exactly what you need.</p>
            <Button asChild className="btn-cta mt-7 bg-primary hover:bg-primary/90 rounded-full px-7 h-12 font-bold"><a href="/contact">Contact Us <ArrowRight className="h-5 w-5 ml-2" /></a></Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Resources;
